import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import _ from 'underscore';

import InformationModal from '../../../components/Modals/informationModal';
import TabSelector from '../../components/TabSelector/tabSelector';
import { DistributionChart } from '../../components/DistributionChart/distributionChart';
import { categories } from '../../../util/appConstants';
import { Checkbox } from 'components/Checkbox/Checkbox';

import {
  SelectTest,
  TestsList,
  SelectedTestsList,
  SelectTestTabContent,
  SelectedTestButton,
  SelectedTestsTableContainer,
  TableHeader,
  TableRow,
  TableCell,
  ArrowUp,
  ArrowDown,
  TableActionButtonContainer,
  TableActionButton,
  TableCellContent,
  Note,
  InformationIcon,
  NoteContainer,
  XIcon,
  Block,
  DropdownButton,
  DropdownMenu,
  Dropdown,
  Divider,
  DropdownItem,
  CustomTimeLimit,
  NewBadge,
  ErrorMessage,
  ModalContentTitle,
  QuestionIcon,
  ModalContentContainer,
  LockIcon,
  PageActionContainer,
} from './styles';

const SelectTestSection = forwardRef(
  (
    { tests, availableLanguages, adaptiveTestInfo, isDemoCompany, toggleTour, selectedTests, setSelectedTests },
    _ref
  ) => {
    const [freeCodeTests, setFreeCodeTests] = useState([]);
    const [isFreeCodeNameAscending, setIsFreeCodeNameAscending] = useState(true);
    const [isFreeCodeDifficultyAscending, setIsFreeCodeDifficultyAscending] = useState(true);
    const [multiChoiceTests, setMultiChoiceTests] = useState([]);
    const [isMultiChoiceNameAscending, setIsMultiChoiceNameAscending] = useState(true);
    const [generalTests, setGeneralTests] = useState([]);
    const [isGeneralNameAscending, setIsGeneralNameAscending] = useState(true);

    const [isInfoOnAdaptiveModalOpen, setIsInfoOnAdaptiveModalOpen] = useState(false);
    const [testInfoModal, setTestInfoModal] = useState({});
    const [testCategoriesData, setTestCategoriesData] = useState([
      { categoryName: 'Code', hasNewTest: false },
      { categoryName: 'Multichoice', hasNewTest: false },
      { categoryName: 'General Skills', hasNewTest: false },
    ]);

    const candidateChoosesTest = {
      _id: 'isCandidateChoosesEnabled',
      name: 'Candidate Chooses',
    };

    useEffect(() => {
      const sortedTests = tests.slice().sort((a, b) => {
        // Put demo tests first then sort by name
        return isDemoCompany && a.isDemoTest !== b.isDemoTest ? (a.isDemoTest ? -1 : 1) : a.name.localeCompare(b.name);
      });

      setFreeCodeTests(sortedTests.filter((test) => test.category === categories.CODE.category));
      setMultiChoiceTests(sortedTests.filter((test) => test.category === categories.MULTICHOICE.category));
      setGeneralTests(sortedTests.filter((test) => test.category === categories.GENERAL_SKILLS.category));
    }, [tests, isDemoCompany]);

    const sortFreeCodeTestsByName = () => {
      setFreeCodeTests(
        freeCodeTests.slice().sort((a, b) => {
          return isFreeCodeNameAscending && b.name.toLowerCase() > a.name.toLowerCase() ? 1 : -1;
        })
      );
      setIsFreeCodeNameAscending(!isFreeCodeNameAscending);
      setIsFreeCodeDifficultyAscending(true);
    };

    const sortFreeCodeTestsByDifficulty = () => {
      const difficultyScale = {
        ['Hard-level']: 3,
        ['Mid-level']: 2,
        ['Easy-level']: 1,
      };
      setFreeCodeTests(
        freeCodeTests.slice().sort((a, b) => {
          return isFreeCodeDifficultyAscending &&
            (difficultyScale[b.difficulty] ?? 2) > (difficultyScale[a.difficulty] ?? 2)
            ? 1
            : -1;
        })
      );
      setIsFreeCodeNameAscending(true);
      setIsFreeCodeDifficultyAscending(!isFreeCodeDifficultyAscending);
    };

    const sortMultiChoiceTestsByName = () => {
      setMultiChoiceTests(
        multiChoiceTests.slice().sort((a, b) => {
          return isMultiChoiceNameAscending && b.name.toLowerCase() > a.name.toLowerCase() ? 1 : -1;
        })
      );
      setIsMultiChoiceNameAscending(!isMultiChoiceNameAscending);
    };

    const sortGeneralTestsByName = () => {
      setGeneralTests(
        generalTests.slice().sort((a, b) => {
          return isGeneralNameAscending && b.name.toLowerCase() > a.name.toLowerCase() ? 1 : -1;
        })
      );
      setIsGeneralNameAscending(!isGeneralNameAscending);
    };

    const toggleTestInSelectedTestList = (test) => {
      let updatedList = [];
      if (testIsSelected(test)) {
        updatedList = [...selectedTests];
        updatedList = updatedList.filter((testItem) => testItem._id !== test._id);
      } else {
        let newTest = { ...test };
        if (test.isDemoTest) newTest.duration = 10;
        newTest.selectedTime = test.duration ?? 50;
        if (test.category === categories.CODE.category) {
          newTest.languages = _.mapObject(availableLanguages[test.executionVersion], () => true);
        }
        updatedList = [...selectedTests, newTest];
      }
      setSelectedTests(updatedList);
    };

    const testIsSelected = (selectedTest) => selectedTests.some((test) => test._id === selectedTest._id);

    const setTestDuration = (testIndex, newDuration) => {
      let updatedList = selectedTests.map((test, index) => {
        if (testIndex === index) return { ...test, selectedTime: newDuration };
        return test;
      });
      setSelectedTests(updatedList);
    };

    const changeTestSelectedLanguage = (index, languageKey) => {
      let updatedList = [...selectedTests];
      updatedList[index].languages[languageKey] = !updatedList[index].languages[languageKey];
      setSelectedTests(updatedList);
    };

    const changeAllTestSelectedLanguage = (index, allSelected) => {
      let updatedList = [...selectedTests];
      _.each(updatedList[index].languages, (val, key) => {
        updatedList[index].languages[key] = !allSelected;
      });
      setSelectedTests(updatedList);
    };

    const renderArrow = (isUp) => {
      return isUp ? <ArrowUp /> : <ArrowDown />;
    };

    const renderInfoOnAdaptiveModal = () => {
      if (isInfoOnAdaptiveModalOpen)
        return (
          <InformationModal
            title="Info on adaptive tests"
            textHTML={adaptiveTestInfo}
            closeModal={() => setIsInfoOnAdaptiveModalOpen(false)}
          />
        );
    };

    const renderTestInfoHTML = () => {
      return (
        <>
          <ModalContentTitle>Distribution</ModalContentTitle>
          <DistributionChart test={testInfoModal} isFreeCode={testInfoModal.category === categories.CODE.category} />
          {testInfoModal.description && (
            <>
              <ModalContentTitle>Test description</ModalContentTitle>
              <ModalContentContainer
                dangerouslySetInnerHTML={{
                  __html: testInfoModal.description,
                }}
              />
            </>
          )}
          {testInfoModal.category === categories.CODE.category && testInfoModal.candidateDescription !== '' && (
            <>
              <ModalContentTitle>Candidate description</ModalContentTitle>
              <ModalContentContainer
                dangerouslySetInnerHTML={{
                  __html: testInfoModal.candidateDescription,
                }}
              />
            </>
          )}
        </>
      );
    };

    const renderFreeCodeTestsTab = () => {
      return (
        <SelectTestTabContent>
          <TableHeader isClickable widthPercentage="40%" onClick={() => sortFreeCodeTestsByName()}>
            Name{renderArrow(isFreeCodeNameAscending)}
          </TableHeader>
          <TableHeader isClickable mobileHide widthPercentage="60%" onClick={() => sortFreeCodeTestsByDifficulty()}>
            Difficulty {renderArrow(isFreeCodeDifficultyAscending)}
          </TableHeader>
          {renderFreeCodeTestsItems()}
        </SelectTestTabContent>
      );
    };

    const renderFreeCodeTestsItems = () => {
      let isSelected = false;
      return freeCodeTests?.map((test) => {
        isSelected = testIsSelected(test);
        const isDemo = isDemoCompany && !test.isDemoTest;
        return (
          <TableRow key={test._id} locked={isDemo}>
            <TableCell widthPercentage="40%">
              {test.name}
              {testIsNew(test) && <NewBadge>New</NewBadge>}
            </TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent>
                <Note>DIFFICULTY</Note>
                {test.difficulty ?? 'Mid-level'}
              </TableCellContent>
              <TableActionButtonContainer>
                <Note
                  isClickable
                  title="Information"
                  onClick={() => !isDemo && setTestInfoModal(test)}
                  disabled={isDemo}
                >
                  About the test <InformationIcon />
                </Note>
                <TableActionButton
                  id="selectTestButton"
                  isSelected={isSelected}
                  onClick={() => toggleTestInSelectedTestList(test)}
                  disabled={isDemo}
                >
                  {isSelected ? 'SELECTED' : 'SELECT TEST'}
                  {isDemo && <LockIcon title="Purchase a test to enable this option" />}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
        );
      });
    };

    const renderMultiChoiceTestsTab = () => {
      let isCandidateChoosesTestSelected = testIsSelected(candidateChoosesTest);
      return (
        <SelectTestTabContent>
          <TableHeader isClickable widthPercentage="40%" onClick={() => sortMultiChoiceTestsByName()}>
            Name{renderArrow(isMultiChoiceNameAscending)}
          </TableHeader>
          <TableHeader isClickable mobileHide widthPercentage="60%" onClick={() => setIsInfoOnAdaptiveModalOpen(true)}>
            Type <InformationIcon />
          </TableHeader>
          <TableRow key={-1} locked={isDemoCompany}>
            <TableCell widthPercentage="40%">{candidateChoosesTest.name}</TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent></TableCellContent>
              <TableActionButtonContainer>
                <Note
                  isClickable
                  title="Allow the candidate to choose which multichoice test to take"
                  disabled={isDemoCompany}
                >
                  About the feature <InformationIcon />
                </Note>
                <TableActionButton
                  isSelected={isCandidateChoosesTestSelected}
                  onClick={() => toggleTestInSelectedTestList(candidateChoosesTest)}
                  disabled={isDemoCompany}
                >
                  {isCandidateChoosesTestSelected ? 'SELECTED' : 'SELECT TEST'}
                  {isDemoCompany && <LockIcon title="Purchase a test to enable this option" />}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
          {renderTestItems(multiChoiceTests)}
        </SelectTestTabContent>
      );
    };

    const renderGeneralTestsTab = () => {
      return (
        <SelectTestTabContent>
          <TableHeader isClickable widthPercentage="40%" onClick={() => sortGeneralTestsByName()}>
            Name{renderArrow(isGeneralNameAscending)}
          </TableHeader>
          <TableHeader isClickable mobileHide widthPercentage="60%" onClick={() => setIsInfoOnAdaptiveModalOpen(true)}>
            Type <InformationIcon />
          </TableHeader>
          {renderTestItems(generalTests)}
        </SelectTestTabContent>
      );
    };

    const renderTestItems = (testList) => {
      let isSelected = false;
      return testList.map((test) => {
        isSelected = testIsSelected(test);
        const isDemo = isDemoCompany && !test.isDemoTest;
        return (
          <TableRow key={test._id} locked={isDemo}>
            <TableCell widthPercentage="40%">
              {test.name}
              {testIsNew(test) && <NewBadge>New</NewBadge>}
            </TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent>{!test.adaptiveTest && 'Non-'}Adaptive</TableCellContent>
              <TableActionButtonContainer>
                <Note
                  isClickable
                  title="Information"
                  onClick={() => !isDemo && setTestInfoModal(test)}
                  disabled={isDemo}
                >
                  About the test <InformationIcon />
                </Note>
                <TableActionButton
                  id="selectTestButton"
                  isSelected={isSelected}
                  onClick={() => toggleTestInSelectedTestList(test)}
                  disabled={isDemo}
                >
                  {isSelected ? 'SELECTED' : 'SELECT TEST'}
                  {isDemo && <LockIcon title="Purchase a test to enable this option" />}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
        );
      });
    };

    const testIsNew = (test) => {
      if (
        test.createdAt === undefined ||
        test.custom === undefined ||
        test.custom ||
        diffDays(new Date(test.createdAt)) > 31
      )
        return false;

      const categoryIndex = testCategoriesData.findIndex((category) => category.categoryName === test.categoryName);
      if (categoryIndex > -1 && !testCategoriesData[categoryIndex].hasNewTest) {
        let updatedData = [...testCategoriesData];
        updatedData[categoryIndex].hasNewTest = true;
        setTestCategoriesData(updatedData);
      }

      return true;
    };

    const diffDays = (date) => {
      return Math.round(new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    };

    const renderTestsTabs = () => {
      return (
        <TabSelector
          items={[
            {
              id: 'testCategories',
              label: renderCategoryLabelNewBadge('FREECODE', testCategoriesData[0].hasNewTest),
              component: renderFreeCodeTestsTab(),
            },
            {
              id: 'testCategories',
              label: renderCategoryLabelNewBadge('MULTICHOICE', testCategoriesData[1].hasNewTest),
              component: renderMultiChoiceTestsTab(),
            },
            {
              id: 'testCategories',
              label: renderCategoryLabelNewBadge('GENERAL', testCategoriesData[2].hasNewTest),
              component: renderGeneralTestsTab(),
            },
          ]}
        ></TabSelector>
      );
    };

    const renderCategoryLabelNewBadge = (label, hasNewTest) => {
      return (
        <>
          {label}
          {hasNewTest && <NewBadge hasPadding>NEW TEST</NewBadge>}
        </>
      );
    };

    const renderSelectedTestsTable = () => {
      return (
        <SelectTestTabContent autoHeight>
          <TableHeader>Chosen tests</TableHeader>
          <TableHeader></TableHeader>
          {renderSelectedTestsItems()}
        </SelectTestTabContent>
      );
    };

    const renderSelectedTestsItems = () => {
      if (selectedTests.length > 0) {
        return selectedTests.map((selectedTest, index) => {
          return (
            <TableRow key={selectedTest._id}>
              <TableCell widthPercentage="30%">
                <Block>
                  {selectedTest.name}
                  {renderAdaptiveTestNote(selectedTest)}
                </Block>
              </TableCell>
              <TableCell widthPercentage="30%" zeroPaddingLeft>
                {renderTimeDropDown(selectedTest, index)}
              </TableCell>
              <TableCell widthPercentage="32%" zeroPaddingLeft verticalTop>
                {renderProgrammingLanguageDropDown(selectedTest, index)}
              </TableCell>
              <TableCell widthPercentage="8%">
                <TableActionButtonContainer widthPercentage="40%">
                  <XIcon onClick={() => toggleTestInSelectedTestList(selectedTest)} />
                </TableActionButtonContainer>
              </TableCell>
            </TableRow>
          );
        });
      } else
        return (
          <TableRow>
            <NoteContainer>
              <Note>You haven't selected any tests yet.</Note>
            </NoteContainer>
          </TableRow>
        );
    };

    const renderAdaptiveTestNote = (selectedTest) => {
      if (selectedTest.adaptiveTest) return <Note>Adaptive Test</Note>;
    };

    const renderTimeDropDown = (selectedTest, index) => {
      const normalDuration = selectedTest.duration ?? 50;
      const offset = normalDuration / 3;
      const ROUND_TO_NEAREST = 5.0;

      // 2/3 (66.67%) of normalDuration, rounded down to nearest multiple of ROUND_TO_NEAREST.
      const shortDuration = Math.floor((normalDuration - offset) / ROUND_TO_NEAREST) * ROUND_TO_NEAREST;
      // 4/3 (133.33%) of normalDuration, rounded up to nearest multiple of ROUND_TO_NEAREST.
      const longDuration = Math.ceil((normalDuration + offset) / ROUND_TO_NEAREST) * ROUND_TO_NEAREST;

      return (
        <Block>
          <Note>TIME LIMIT</Note>
          <Dropdown>
            <DropdownButton widthSize="95px" id="timeLimitDropdownlist" data-toggle="dropdown" disabled={isDemoCompany}>
              {selectedTest.selectedTime} min <ArrowDown />
            </DropdownButton>
            <DropdownMenu>
              <Note>Standard time limits</Note>
              <DropdownItem onClick={() => setTestDuration(index, shortDuration)}>{shortDuration} minutes</DropdownItem>
              <DropdownItem onClick={() => setTestDuration(index, normalDuration)}>
                {normalDuration} minutes (recommended)
              </DropdownItem>
              <DropdownItem onClick={() => setTestDuration(index, longDuration)}>{longDuration} minutes</DropdownItem>
              <Divider />
              <Note>Custom time limit</Note>
              <DropdownItem selected>
                <CustomTimeLimit
                  value={selectedTest.selectedTime}
                  onChange={(e) => setTestDuration(index, e.target.value)}
                ></CustomTimeLimit>
                minutes
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          {(!selectedTest.selectedTime || selectedTest.selectedTime < 5) && (
            <ErrorMessage small noMarginBottom>
              Time is not valid
            </ErrorMessage>
          )}
        </Block>
      );
    };

    const renderProgrammingLanguageDropDown = (selectedTest, index) => {
      if (selectedTest.category === categories.CODE.category) {
        const selectedCount = _.filter(selectedTest.languages, (val) => val).length;
        const allSelected = _.keys(selectedTest.languages).length === selectedCount;
        return (
          <Block>
            <Note fontSize="10px">ALLOWED LANGUAGES</Note>
            <Dropdown>
              <DropdownButton widthSize="116px" data-toggle="dropdown">
                {selectedCount > 0 ? `${selectedCount} selected` : 'Choose'} <ArrowDown />
              </DropdownButton>
              <DropdownMenu dropdownRight>
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    label={allSelected ? 'Unselect all' : 'Select all'}
                    onChange={() => changeAllTestSelectedLanguage(index, allSelected)}
                  />
                </DropdownItem>
                {_.map(availableLanguages[selectedTest.executionVersion], (val, key) => {
                  return (
                    <DropdownItem
                      key={key}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={selectedTest.languages[key]}
                        label={val.CurrentVersion}
                        onChange={() => {
                          changeTestSelectedLanguage(index, key);
                        }}
                      />
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </Dropdown>
            {selectedCount === 0 && (
              <ErrorMessage small noMarginBottom>
                Please select language
              </ErrorMessage>
            )}
          </Block>
        );
      }
    };

    return (
      <SelectTest>
        <PageActionContainer>
          {!isDemoCompany && (
            <SelectedTestButton href="/customTests" title="Create your own test with questions of your own liking">
              CREATE CUSTOMIZED TEST
            </SelectedTestButton>
          )}
          <SelectedTestButton width="30px" onClick={toggleTour}>
            <QuestionIcon />
          </SelectedTestButton>
        </PageActionContainer>
        <TestsList>{renderTestsTabs()}</TestsList>
        <SelectedTestsList>
          <SelectedTestsTableContainer id="selectedTestsTable">
            {renderSelectedTestsTable()}
          </SelectedTestsTableContainer>
        </SelectedTestsList>
        {renderInfoOnAdaptiveModal()}
        {_.keys(testInfoModal).length > 0 && (
          <InformationModal
            title={testInfoModal.name}
            text={renderTestInfoHTML()}
            closeModal={() => setTestInfoModal({})}
          />
        )}
      </SelectTest>
    );
  }
);

export default SelectTestSection;
